import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
    ClipboardList,
    Loader2,
    AlertCircle,
    PlusCircle,
    Trash2,
    X,
    Save,
    Calendar,
    User,
    Users,
    FileEdit,
    Settings,
    Play,
    Search,
    ChevronLeft,
    ChevronRight,
    ChevronDown,
    ChevronUp,
    Award,
    CheckCircle2,
    History,
    Layers,
    ArrowUpDown,
    Building2
} from 'lucide-react';
import { apiClient } from '../utils/apiClient';
import { useAuth } from '../contexts/AuthContext';

// Hareket tipi string karşılığı (badge için)
const hareketTipiLabel = {
    1: 'Manuel Artış',
    2: 'Manuel Azalış',
    3: 'Düzeltme',
    4: 'Otomatik Hakediş',
    5: 'İzin Kullanımı',
    6: 'İzin İptali İadesi',
    7: 'Devir Süre Sonu / Silme',
    ManuelArtis: 'Manuel Artış',
    ManuelAzalis: 'Manuel Azalış',
    Duzeltme: 'Düzeltme',
    OtomatikHakedis: 'Otomatik Hakediş',
    IzinKullanimi: 'İzin Kullanımı',
    IzinIptali: 'İzin İptali İadesi',
    DevirSilme: 'Devir Süre Sonu / Silme'
};

const hareketTipiRenk = {
    1: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
    2: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
    3: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
    4: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200',
    5: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
    6: 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200',
    7: 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
    ManuelArtis: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
    ManuelAzalis: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
    Duzeltme: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
    OtomatikHakedis: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200',
    IzinKullanimi: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
    IzinIptali: 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200',
    DevirSilme: 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
};

const isManualMovementType = (t) =>
    t === 1 || t === 2 || t === 3 ||
    t === 'ManuelArtis' || t === 'ManuelAzalis' || t === 'Duzeltme';

const bakiyeModuLabel = {
    0: 'Bakiyesiz',
    1: 'Yıllık Hak Ediş',
    2: 'Dönemsel / Olay Limitli',
    Bakiyesiz: 'Bakiyesiz',
    Yillik: 'Yıllık Hak Ediş',
    EventLimit: 'Dönemsel / Olay Limitli'
};

const donemTipiLabel = {
    1: 'Yıllık',
    2: 'Aylık',
    3: 'Tek Seferlik',
    Yillik: 'Yıllık',
    Aylik: 'Aylık',
    TekSeferlik: 'Tek Seferlik'
};

// ------------------------------------------------------------
// 10.000+ Personel Destekli Sunucu Taraflı Arama Seçicisi
// ------------------------------------------------------------
const ServerEmployeeSelect = ({ value, selectedLabel, onSelect, placeholder = 'Personel ara (Ad, Soyad, Sicil)...', allowAll = true }) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        const timer = setTimeout(async () => {
            try {
                setLoading(true);
                const qs = query.trim() ? `&search=${encodeURIComponent(query.trim())}` : '';
                const res = await apiClient(`employee?page=1&pageSize=25${qs}`);
                if (cancelled) return;
                const list = res?.items || res?.data || (Array.isArray(res) ? res : []);
                setResults(
                    list.map(e => ({
                        id: e.id,
                        adSoyad: [e.firstName, e.lastName].filter(Boolean).join(' ') || e.fullName || `ID: ${e.id}`,
                        sicilNo: e.sicilNo || '',
                        departmentName: e.departmentName || e.department?.name || ''
                    }))
                );
            } catch (err) {
                if (!cancelled) setResults([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, 250);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [open, query]);

    return (
        <div className="relative" ref={containerRef}>
            <div
                onClick={() => setOpen(prev => !prev)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 flex items-center justify-between cursor-pointer hover:border-indigo-400 transition-colors"
            >
                <span className="truncate">
                    {value ? (selectedLabel || `Personel #${value}`) : (allowAll ? 'Tüm Personeller' : placeholder)}
                </span>
                <div className="flex items-center gap-1 ml-2 shrink-0">
                    {value && (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                onSelect('', '');
                            }}
                            className="p-0.5 text-gray-400 hover:text-red-500 rounded"
                            title="Seçimi temizle"
                        >
                            <X className="w-3.5 h-3.5" />
                        </button>
                    )}
                    <Search className="w-4 h-4 text-gray-400" />
                </div>
            </div>

            {open && (
                <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl overflow-hidden">
                    <div className="p-2 border-b border-gray-100 dark:border-gray-800">
                        <div className="relative">
                            <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-2.5" />
                            <input
                                type="text"
                                autoFocus
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder={placeholder}
                                className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 outline-none focus:border-indigo-500"
                            />
                        </div>
                    </div>
                    <div className="max-h-60 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800">
                        {allowAll && (
                            <button
                                type="button"
                                onClick={() => {
                                    onSelect('', '');
                                    setOpen(false);
                                }}
                                className="w-full text-left px-3 py-2 text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
                            >
                                Tüm Personeller
                            </button>
                        )}
                        {loading ? (
                            <div className="py-4 text-center text-xs text-gray-500 flex items-center justify-center gap-2">
                                <Loader2 className="w-4 h-4 animate-spin text-indigo-500" />
                                Aranıyor...
                            </div>
                        ) : results.length === 0 ? (
                            <div className="py-4 text-center text-xs text-gray-500">
                                Eşleşen personel bulunamadı.
                            </div>
                        ) : (
                            results.map(emp => (
                                <button
                                    key={emp.id}
                                    type="button"
                                    onClick={() => {
                                        onSelect(String(emp.id), emp.adSoyad);
                                        setOpen(false);
                                    }}
                                    className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center justify-between text-xs"
                                >
                                    <div>
                                        <div className="font-semibold text-gray-900 dark:text-gray-100">{emp.adSoyad}</div>
                                        {emp.departmentName && (
                                            <div className="text-[10px] text-gray-500 dark:text-gray-400">{emp.departmentName}</div>
                                        )}
                                    </div>
                                    {emp.sicilNo && (
                                        <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                                            {emp.sicilNo}
                                        </span>
                                    )}
                                </button>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// -----------------------------
// Otomatik yıllık izin modalı
// -----------------------------
const AutoAnnualModal = ({
    show,
    onClose,
    onSubmit,
    isSubmitting,
    currentSettings
}) => {
    const [aktifMi, setAktifMi] = useState(true);
    const [startDate, setStartDate] = useState('');
    const [simdiHesapla, setSimdiHesapla] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (show) {
            setAktifMi(currentSettings?.yillikIzinOtomatikHakedisAktifMi ?? true);
            const rawDate = currentSettings?.yillikIzinOtomatikBaslangicTarihi;
            if (rawDate) {
                const d = new Date(rawDate);
                const tzOffset = d.getTimezoneOffset() * 60000;
                setStartDate(new Date(d.getTime() - tzOffset).toISOString().slice(0, 10));
            } else {
                setStartDate(new Date().toISOString().slice(0, 10));
            }
            setSimdiHesapla(true);
            setError(null);
        }
    }, [show, currentSettings]);

    if (!show) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (aktifMi && !startDate) {
            setError('Sistem aktifken başlangıç tarihi seçmelisiniz.');
            return;
        }

        try {
            await onSubmit({ baslangicTarihi: startDate, aktifMi, simdiHesapla }, setError);
        } catch (err) {
            console.error(err);
            setError('İşlem sırasında bir hata oluştu.');
        }
    };

    return (
        <div className="fixed inset-0 z-[60] flex justify-center items-start overflow-y-auto p-4 md:p-8">
            <div
                className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />

            <div className="relative z-10 w-full max-w-lg bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800 overflow-hidden my-auto">
                <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/50">
                    <div className="flex items-center space-x-3">
                        <div className="p-2 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg text-emerald-600 dark:text-emerald-400">
                            <Settings className="w-5 h-5" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                            Otomatik Yıllık İzin Hakediş Ayarları
                        </h3>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-2 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6">
                    {error && (
                        <div className="mb-4 flex items-center bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-200 px-4 py-3 rounded-xl text-xs font-medium">
                            <AlertCircle className="w-4 h-4 mr-2 shrink-0 text-red-500" />
                            <span>{error}</span>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <label className="flex items-center justify-between p-3.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/70 dark:bg-gray-800/50 cursor-pointer">
                            <div>
                                <span className="text-sm font-bold text-gray-900 dark:text-gray-100 block">
                                    Otomatik Hakediş Motoru Aktif
                                </span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                    Her gece 02:00&apos;de yıl dönümü gelen personellere yıllık izin hakkı tanımlanır.
                                </span>
                            </div>
                            <input
                                type="checkbox"
                                checked={aktifMi}
                                onChange={(e) => setAktifMi(e.target.checked)}
                                className="h-5 w-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                            />
                        </label>

                        <div>
                            <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center">
                                <Calendar className="w-3.5 h-3.5 mr-1.5 text-emerald-500" />
                                Otomatik Hakediş Başlangıç Tarihi
                            </label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="block w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50/50 dark:bg-gray-800/50 text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none"
                            />
                            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-2 leading-relaxed">
                                Seçilen bu tarihten <strong>sonraki</strong> tüm yıl dönümleri (geçmiş ara yıllar dahil) sistem tarafından kıdem ve yaş kurallarına göre otomatik hesaplanır. Bu tarihten önceki dönemler manuel devir kabul edilir.
                            </p>
                        </div>

                        {aktifMi && (
                            <label className="flex items-center gap-2.5 p-3 rounded-xl border border-emerald-200 dark:border-emerald-800/50 bg-emerald-50/50 dark:bg-emerald-900/20 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={simdiHesapla}
                                    onChange={(e) => setSimdiHesapla(e.target.checked)}
                                    className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                                />
                                <span className="text-xs font-medium text-emerald-900 dark:text-emerald-200">
                                    Kaydettikten hemen sonra eksik yıllık izin hakedişlerini şimdi hesapla
                                </span>
                            </label>
                        )}

                        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-800">
                            <button
                                type="button"
                                onClick={onClose}
                                disabled={isSubmitting}
                                className="px-5 py-2 rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 text-sm font-semibold hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                            >
                                İptal
                            </button>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="flex items-center px-6 py-2 rounded-xl bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 disabled:bg-emerald-400 shadow-lg shadow-emerald-500/20 transition-all"
                            >
                                {isSubmitting ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                    <Save className="w-4 h-4 mr-2" />
                                )}
                                Ayarları Kaydet
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

// -----------------------------
// Manuel bakiye düzeltme modalı
// -----------------------------
const ManualAdjustModal = ({
    show,
    onClose,
    onSubmit,
    izinTurleri,
    initialContext,
    isSubmitting
}) => {
    const [form, setForm] = useState({
        employeeId: '',
        employeeLabel: '',
        izinTuruId: '',
        yil: new Date().getFullYear(),
        hareketTipi: 'ManuelArtis',
        degisimGun: '',
        degisimDakika: '',
        aciklama: ''
    });

    const [error, setError] = useState(null);

    useEffect(() => {
        if (show) {
            setForm({
                employeeId: initialContext?.employeeId ? String(initialContext.employeeId) : '',
                employeeLabel: initialContext?.employeeLabel || '',
                izinTuruId: initialContext?.izinTuruId || '',
                yil: initialContext?.yil || new Date().getFullYear(),
                hareketTipi: 'ManuelArtis',
                degisimGun: '',
                degisimDakika: '',
                aciklama: ''
            });
            setError(null);
        }
    }, [show, initialContext]);

    if (!show) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!form.employeeId) {
            setError('Personel seçmelisiniz.');
            return;
        }
        if (!form.izinTuruId) {
            setError('İzin türü seçmelisiniz.');
            return;
        }

        const hasGun = form.degisimGun !== '' && form.degisimGun !== null;
        const hasDakika = form.degisimDakika !== '' && form.degisimDakika !== null;

        if (!hasGun && !hasDakika) {
            setError('En azından gün veya dakika alanlarından birini doldurmalısınız.');
            return;
        }

        const payload = {
            employeeId: Number(form.employeeId),
            izinTuruId: Number(form.izinTuruId),
            yil: form.yil ? Number(form.yil) : null,
            hareketTipi: form.hareketTipi,
            degisimGun: hasGun ? Number(form.degisimGun) : null,
            degisimDakika: hasDakika ? Number(form.degisimDakika) : null,
            aciklama: form.aciklama || null
        };

        await onSubmit(payload, () => {}, setError);
    };

    return (
        <div className="fixed inset-0 z-[60] flex justify-center items-start overflow-y-auto p-4 md:p-8">
            <div
                className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />

            <div className="relative z-10 w-full max-w-xl bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800 overflow-hidden my-auto flex flex-col max-h-[90vh]">
                <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/50">
                    <div className="flex items-center space-x-3">
                        <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg text-blue-600 dark:text-blue-400">
                            <FileEdit className="w-5 h-5" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                                Manuel İzin Bakiyesi Hareketi
                            </h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                Personele ek izin tanımlayın veya mevcut bakiyeden düşüm yapın
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-2 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 overflow-y-auto">
                    {error && (
                        <div className="mb-6 flex items-center bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-200 px-4 py-3 rounded-xl text-xs font-medium">
                            <AlertCircle className="w-4 h-4 mr-2 shrink-0 text-red-500" />
                            <span>{error}</span>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div>
                            <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center">
                                <User className="w-3.5 h-3.5 mr-1.5 text-blue-500" />
                                Personel Seçimi
                            </label>
                            <ServerEmployeeSelect
                                value={form.employeeId}
                                selectedLabel={form.employeeLabel}
                                allowAll={false}
                                placeholder="Personel seçin veya arayın..."
                                onSelect={(id, label) => setForm(prev => ({ ...prev, employeeId: id, employeeLabel: label }))}
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                                    İzin Türü
                                </label>
                                <select
                                    name="izinTuruId"
                                    value={form.izinTuruId}
                                    onChange={handleChange}
                                    className="block w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50/50 dark:bg-gray-800/50 text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                                >
                                    <option value="">Seçiniz...</option>
                                    {izinTurleri.map(t => (
                                        <option key={t.id} value={t.id}>
                                            {t.ad} ({t.kod})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center">
                                    <Calendar className="w-3.5 h-3.5 mr-1.5 text-blue-500" />
                                    Hakediş Yılı
                                </label>
                                <input
                                    type="number"
                                    name="yil"
                                    value={form.yil}
                                    onChange={handleChange}
                                    className="block w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50/50 dark:bg-gray-800/50 text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                                    placeholder="Örn: 2026"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                                İşlem Tipi
                            </label>
                            <div className="grid grid-cols-3 gap-2">
                                {[
                                    { id: 'ManuelArtis', label: 'Artış (+)', color: 'peer-checked:bg-emerald-600 peer-checked:text-white peer-checked:border-emerald-600' },
                                    { id: 'ManuelAzalis', label: 'Azalış (-)', color: 'peer-checked:bg-red-600 peer-checked:text-white peer-checked:border-red-600' },
                                    { id: 'Duzeltme', label: 'Düzeltme', color: 'peer-checked:bg-amber-500 peer-checked:text-white peer-checked:border-amber-500' }
                                ].map((type) => (
                                    <label key={type.id} className="cursor-pointer">
                                        <input
                                            type="radio"
                                            name="hareketTipi"
                                            value={type.id}
                                            checked={form.hareketTipi === type.id}
                                            onChange={handleChange}
                                            className="sr-only peer"
                                        />
                                        <div className={`text-center py-2.5 px-3 rounded-xl border border-gray-200 dark:border-gray-700 text-xs font-bold text-gray-600 dark:text-gray-400 bg-gray-50/30 dark:bg-gray-800/30 transition-all hover:bg-gray-100 dark:hover:bg-gray-800 ${type.color}`}>
                                            {type.label}
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-blue-50/30 dark:bg-blue-900/10 p-4 rounded-2xl border border-blue-100/50 dark:border-blue-900/20">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                                    Değişim (Gün)
                                </label>
                                <input
                                    type="number"
                                    step="0.25"
                                    name="degisimGun"
                                    value={form.degisimGun}
                                    onChange={handleChange}
                                    placeholder="Örn: 14 veya 1.5"
                                    className="block w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none font-mono"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                                    Değişim (Dakika)
                                </label>
                                <input
                                    type="number"
                                    name="degisimDakika"
                                    value={form.degisimDakika}
                                    onChange={handleChange}
                                    placeholder="Alternatif: Dakika"
                                    className="block w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none font-mono"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                                Açıklama / Not
                            </label>
                            <textarea
                                name="aciklama"
                                value={form.aciklama}
                                onChange={handleChange}
                                rows={3}
                                placeholder="İşlem nedenini buraya yazabilirsiniz..."
                                className="block w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50/50 dark:bg-gray-800/50 text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none resize-none"
                            />
                        </div>

                        <div className="flex justify-end gap-3 pt-4 sticky bottom-0 bg-white dark:bg-gray-900 -mx-6 -mb-6 px-6 py-4 border-t border-gray-100 dark:border-gray-800">
                            <button
                                type="button"
                                onClick={onClose}
                                disabled={isSubmitting}
                                className="px-5 py-2 rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 text-sm font-semibold hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                            >
                                İptal
                            </button>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="flex items-center px-6 py-2 rounded-xl bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 disabled:bg-blue-400 shadow-lg shadow-blue-500/20 transition-all"
                            >
                                {isSubmitting ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                    <Save className="w-4 h-4 mr-2" />
                                )}
                                Kaydet
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default function LeaveBalanceManagement() {
    const { permissions } = useAuth();

    const canView = useMemo(
        () => permissions.includes('leave_balances:view'),
        [permissions]
    );
    const canManage = useMemo(
        () => permissions.includes('leave_balances:manage'),
        [permissions]
    );

    // Görünüm Sekmesi: 'summary' (Kişi Bazlı Toplam Bakiye Özeti - Varsayılan) | 'movements' (Detaylı Yıl/İşlem Defteri)
    const [activeTab, setActiveTab] = useState('summary');

    // --- SEKME 1: KİŞİ BAZLI TOPLAM BAKİYE ÖZETİ STATE'LERİ ---
    const [summaryLoading, setSummaryLoading] = useState(true);
    const [summaryItems, setSummaryItems] = useState([]);
    const [expandedEmployeeIds, setExpandedEmployeeIds] = useState({});
    const [departments, setDepartments] = useState([]);
    const [summaryFilters, setSummaryFilters] = useState({
        search: '',
        departmentId: '',
        izinTuruId: '',
        balanceStatus: 'all',
        sortBy: 'remaining_desc'
    });
    const [summaryPagination, setSummaryPagination] = useState({
        page: 1,
        pageSize: 50,
        totalCount: 0,
        totalPages: 1,
        grandTotalEntitledDays: 0,
        grandTotalUsedDays: 0,
        grandTotalRemainingDays: 0,
        employeesWithPositiveBalance: 0,
        employeesWithZeroOrNegativeBalance: 0
    });

    // --- SEKME 2: DETAYLI HAREKET DEFTERİ STATE'LERİ ---
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);

    const [movements, setMovements] = useState([]);
    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 50,
        totalCount: 0,
        totalPages: 1,
        totalNetMinutes: 0,
        totalNetDays: 0
    });

    const [izinTurleri, setIzinTurleri] = useState([]);

    const [filters, setFilters] = useState({
        employeeId: '',
        employeeLabel: '',
        izinTuruId: '',
        yil: '',
        hareketTipi: '',
        search: ''
    });

    // Seçili personelin güncel izin bakiyeleri ve kıdem özeti
    const [employeeBalances, setEmployeeBalances] = useState([]);
    const [employeeSeniority, setEmployeeSeniority] = useState(null);
    const [employeeBalancesLoading, setEmployeeBalancesLoading] = useState(false);
    const [employeeBalancesError, setEmployeeBalancesError] = useState(null);

    // Firma bazlı otomatik yıllık izin ayarları
    const [settings, setSettings] = useState(null);
    const [settingsLoading, setSettingsLoading] = useState(true);
    const [settingsError, setSettingsError] = useState(null);
    const [isAutoAnnualModalOpen, setIsAutoAnnualModalOpen] = useState(false);
    const [isSavingAutoAnnual, setIsSavingAutoAnnual] = useState(false);
    const [isRunningAutoNow, setIsRunningAutoNow] = useState(false);

    const [isManualModalOpen, setIsManualModalOpen] = useState(false);
    const [manualContext, setManualContext] = useState(null);
    const [isSubmittingManual, setIsSubmittingManual] = useState(false);

    const fetchIzinTurleri = useCallback(async () => {
        try {
            const data = await apiClient('izinturu');
            setIzinTurleri(data || []);
        } catch (err) {
            console.error('İzin türleri yüklenirken hata:', err);
        }
    }, []);

    const fetchDepartments = useCallback(async () => {
        try {
            const data = await apiClient('departments');
            setDepartments(Array.isArray(data) ? data : (data?.items || []));
        } catch (err) {
            console.error('Departmanlar yüklenirken hata:', err);
        }
    }, []);

    const fetchSettings = useCallback(async () => {
        if (!canView) {
            setSettingsLoading(false);
            return;
        }
        setSettingsLoading(false);
        setSettingsError(null);
        try {
            setSettingsLoading(true);
            const data = await apiClient('leave-balances/settings');
            setSettings(data || null);
        } catch (err) {
            console.error('Ayarlar yüklenirken hata:', err);
            setSettingsError(err.message || 'Firma izin ayarları yüklenirken hata oluştu.');
        } finally {
            setSettingsLoading(false);
        }
    }, [canView]);

    const fetchEmployeeSummary = useCallback(async (targetPage = 1, customPageSize = null) => {
        if (!canView) {
            setSummaryLoading(false);
            return;
        }
        setSummaryLoading(true);
        setError(null);
        try {
            const ps = customPageSize || summaryPagination.pageSize;
            const query = [`page=${targetPage}`, `pageSize=${ps}`];
            if (summaryFilters.search?.trim()) query.push(`search=${encodeURIComponent(summaryFilters.search.trim())}`);
            if (summaryFilters.departmentId) query.push(`departmentId=${summaryFilters.departmentId}`);
            if (summaryFilters.izinTuruId) query.push(`izinTuruId=${summaryFilters.izinTuruId}`);
            if (summaryFilters.balanceStatus && summaryFilters.balanceStatus !== 'all') {
                query.push(`balanceStatus=${summaryFilters.balanceStatus}`);
            }
            if (summaryFilters.sortBy) query.push(`sortBy=${summaryFilters.sortBy}`);

            const data = await apiClient(`leave-balances/summary?${query.join('&')}`);
            setSummaryItems(data?.items || []);
            setSummaryPagination(prev => ({
                ...prev,
                page: data?.page || targetPage,
                pageSize: ps,
                totalCount: data?.totalCount || 0,
                totalPages: Math.max(1, data?.totalPages || 1),
                grandTotalEntitledDays: data?.grandTotalEntitledDays || 0,
                grandTotalUsedDays: data?.grandTotalUsedDays || 0,
                grandTotalRemainingDays: data?.grandTotalRemainingDays || 0,
                employeesWithPositiveBalance: data?.employeesWithPositiveBalance || 0,
                employeesWithZeroOrNegativeBalance: data?.employeesWithZeroOrNegativeBalance || 0
            }));
        } catch (err) {
            console.error('Personel bakiye özeti yüklenirken hata:', err);
            setError(err.message || 'Personel bakiye özeti yüklenirken bir hata oluştu.');
        } finally {
            setSummaryLoading(false);
        }
    }, [canView, summaryFilters, summaryPagination.pageSize]);

    const fetchMovements = useCallback(async (targetPage = 1, customPageSize = null) => {
        if (!canView) {
            setLoading(false);
            setError('İzin bakiyesi hareketlerini görüntüleme yetkiniz bulunmamaktadır.');
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const ps = customPageSize || pagination.pageSize;
            const query = [`page=${targetPage}`, `pageSize=${ps}`];
            if (filters.employeeId) query.push(`employeeId=${filters.employeeId}`);
            if (filters.izinTuruId) query.push(`izinTuruId=${filters.izinTuruId}`);
            if (filters.yil) query.push(`yil=${filters.yil}`);
            if (filters.hareketTipi) query.push(`hareketTipi=${filters.hareketTipi}`);
            if (filters.search?.trim()) query.push(`search=${encodeURIComponent(filters.search.trim())}`);

            const qs = `?${query.join('&')}`;
            const data = await apiClient(`leave-balances/manual${qs}`);

            if (data && Array.isArray(data.items)) {
                setMovements(data.items);
                setPagination(prev => ({
                    ...prev,
                    page: data.page || targetPage,
                    pageSize: ps,
                    totalCount: data.totalCount || 0,
                    totalPages: Math.max(1, data.totalPages || 1),
                    totalNetMinutes: data.totalNetMinutes || 0,
                    totalNetDays: data.totalNetDays || 0
                }));
            } else {
                const arr = Array.isArray(data) ? data : [];
                setMovements(arr);
                const netMin = arr.reduce((s, m) => s + (m.miktarDakika || 0), 0);
                setPagination(prev => ({
                    ...prev,
                    page: 1,
                    pageSize: ps,
                    totalCount: arr.length,
                    totalPages: 1,
                    totalNetMinutes: netMin,
                    totalNetDays: netMin / 480.0
                }));
            }
        } catch (err) {
            console.error(err);
            setError(err.message || 'İzin bakiyesi hareketleri yüklenirken bir hata oluştu.');
        } finally {
            setLoading(false);
        }
    }, [canView, filters, pagination.pageSize]);

    const fetchSelectedEmployeeDetails = useCallback(async (empId) => {
        if (!canView || !empId) {
            setEmployeeBalances([]);
            setEmployeeSeniority(null);
            setEmployeeBalancesLoading(false);
            return;
        }

        try {
            setEmployeeBalancesLoading(true);
            setEmployeeBalancesError(null);
            const [balData, senData] = await Promise.all([
                apiClient(`izin-bakiyeleri/personel/${empId}`),
                apiClient(`Employee/${empId}/seniority-summary`).catch(() => null)
            ]);
            setEmployeeBalances(balData || []);
            setEmployeeSeniority(senData || null);
        } catch (err) {
            console.error('Personel izin bakiyeleri yüklenirken hata:', err);
            setEmployeeBalancesError(err.message || 'Personel izin bakiyeleri yüklenirken bir hata oluştu.');
        } finally {
            setEmployeeBalancesLoading(false);
        }
    }, [canView]);

    useEffect(() => {
        if (!canView) {
            setLoading(false);
            setSummaryLoading(false);
            setSettingsLoading(false);
            return;
        }
        fetchIzinTurleri();
        fetchDepartments();
        fetchSettings();
    }, [canView, fetchIzinTurleri, fetchDepartments, fetchSettings]);

    useEffect(() => {
        if (canView) {
            fetchEmployeeSummary(1);
        }
    }, [canView, fetchEmployeeSummary]);

    useEffect(() => {
        if (canView) {
            fetchMovements(1);
        }
    }, [canView, fetchMovements]);

    useEffect(() => {
        if (canView) {
            fetchSelectedEmployeeDetails(filters.employeeId);
        }
    }, [canView, filters.employeeId, fetchSelectedEmployeeDetails]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleResetFilters = () => {
        setFilters({
            employeeId: '',
            employeeLabel: '',
            izinTuruId: '',
            yil: '',
            hareketTipi: '',
            search: ''
        });
    };

    const handleResetSummaryFilters = () => {
        setSummaryFilters({
            search: '',
            departmentId: '',
            izinTuruId: '',
            balanceStatus: 'all',
            sortBy: 'remaining_desc'
        });
    };

    const toggleExpandEmployee = (empId) => {
        setExpandedEmployeeIds(prev => ({
            ...prev,
            [empId]: !prev[empId]
        }));
    };

    const switchToEmployeeMovements = (empId, empName) => {
        setFilters(prev => ({
            ...prev,
            employeeId: String(empId),
            employeeLabel: empName
        }));
        setActiveTab('movements');
    };

    const openManualModal = (context) => {
        setManualContext(context || null);
        setIsManualModalOpen(true);
    };

    const handleManualSubmit = async (payload, onSuccess, setFormError) => {
        setIsSubmittingManual(true);
        setFormError(null);
        setMessage(null);
        try {
            await apiClient('leave-balances/manual', {
                method: 'POST',
                body: payload
            });
            setMessage('Manuel bakiye hareketi başarıyla kaydedildi.');
            onSuccess();
            setIsManualModalOpen(false);
            fetchEmployeeSummary(summaryPagination.page);
            fetchMovements(pagination.page);
            if (filters.employeeId) fetchSelectedEmployeeDetails(filters.employeeId);
        } catch (err) {
            console.error(err);
            setFormError(err.message || 'Manuel hareket kaydedilirken bir hata oluştu.');
        } finally {
            setIsSubmittingManual(false);
        }
    };

    const handleDeleteMovement = async (movement) => {
        if (!window.confirm('Bu manuel hareket kaydını silmek istediğinize emin misiniz?')) {
            return;
        }
        setMessage(null);
        setError(null);
        try {
            await apiClient(`leave-balances/manual/${movement.id}`, {
                method: 'DELETE'
            });
            setMessage('Manuel hareket kaydı başarıyla silindi.');
            fetchEmployeeSummary(summaryPagination.page);
            fetchMovements(pagination.page);
            if (filters.employeeId) fetchSelectedEmployeeDetails(filters.employeeId);
        } catch (err) {
            console.error(err);
            setError(err.message || 'Kayıt silinirken bir hata oluştu.');
        }
    };

    const handleAutoAnnualSubmit = async (payload, setFormError) => {
        setIsSavingAutoAnnual(true);
        setFormError(null);
        setMessage(null);
        try {
            const resp = await apiClient('leave-balances/enable-auto-annual', {
                method: 'POST',
                body: payload
            });
            const granted = resp?.runResult?.grantedEntitlementCount || 0;
            const grantedDays = resp?.runResult?.totalGrantedDays || 0;
            setMessage(
                payload.aktifMi
                    ? `Otomatik yıllık izin hakediş ayarları güncellendi.${granted > 0 ? ` (${granted} yeni dönem hakedişi / +${grantedDays} gün tanımlandı)` : ''}`
                    : 'Otomatik yıllık izin hakediş sistemi pasife alındı.'
            );
            setIsAutoAnnualModalOpen(false);
            await fetchSettings();
            await fetchEmployeeSummary(1);
            await fetchMovements(1);
            if (filters.employeeId) fetchSelectedEmployeeDetails(filters.employeeId);
        } catch (err) {
            console.error(err);
            setFormError(err.message || 'Otomatik hakediş ayarları kaydedilirken bir hata oluştu.');
        } finally {
            setIsSavingAutoAnnual(false);
        }
    };

    const handleRunAutoAnnualNow = async () => {
        setIsRunningAutoNow(true);
        setMessage(null);
        setError(null);
        try {
            const resp = await apiClient('leave-balances/run-auto-annual', {
                method: 'POST'
            });
            setMessage(resp?.message || 'Yıllık izin hakediş hesaplaması başarıyla çalıştırıldı.');
            await fetchEmployeeSummary(1);
            await fetchMovements(1);
            if (filters.employeeId) fetchSelectedEmployeeDetails(filters.employeeId);
        } catch (err) {
            console.error(err);
            setError(err.message || 'Hakediş hesaplaması çalıştırılırken hata oluştu.');
        } finally {
            setIsRunningAutoNow(false);
        }
    };

    if (!canView) {
        return (
            <div className="p-6 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700">
                <div className="flex items-center text-red-600 dark:text-red-300">
                    <AlertCircle className="w-6 h-6 mr-2" />
                    <p className="text-sm">
                        İzin bakiyesi hareketlerini görüntüleme yetkiniz bulunmamaktadır.
                    </p>
                </div>
            </div>
        );
    }

    const autoAnnualActive = settings?.yillikIzinOtomatikHakedisAktifMi;
    const autoAnnualDate = settings?.yillikIzinOtomatikBaslangicTarihi
        ? new Date(settings.yillikIzinOtomatikBaslangicTarihi).toLocaleDateString('tr-TR')
        : null;

    const getGunSaatForIzinTuru = izinTuruId => {
        const t = izinTurleri.find(it => String(it.id) === String(izinTuruId));
        if (!t || t.varsayilanGunSaat == null || t.varsayilanGunSaat <= 0) return 8;
        return t.varsayilanGunSaat;
    };

    const mapBalanceRow = b => {
        const gunSaat = getGunSaatForIzinTuru(b.izinTuruId);
        const dakikaToGun = dk => (dk || 0) / (gunSaat * 60);

        return {
            ...b,
            toplamGun: dakikaToGun(b.toplamHakDakika),
            kullanilanGun: dakikaToGun(b.kullanilanHakDakika),
            kalanGun: dakikaToGun(b.kalanHakDakika)
        };
    };

    const mappedEmployeeBalances = employeeBalances.map(mapBalanceRow);
    const totalEmployeeRemainingDays = mappedEmployeeBalances.reduce((sum, b) => sum + (b.kalanGun || 0), 0);

    return (
        <div className="w-full bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 border border-gray-200 dark:border-gray-700">
            {/* Başlık + üst aksiyon */}
            <div className="flex flex-col gap-4 mb-6">
                <div className="flex flex-wrap justify-between items-center gap-3">
                    <div>
                        <h2 className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-gray-100 flex items-center gap-3">
                            <ClipboardList className="w-8 h-8 text-purple-500" />
                            İzin Bakiye Yönetimi ve Hakediş Merkezi
                        </h2>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Personel bazlı toplam net izin bakiyeleri, yıl kovaları ve detaylı hakediş/kullanım hareketleri
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        {canManage && (
                            <button
                                type="button"
                                onClick={() => openManualModal(filters.employeeId ? { employeeId: filters.employeeId, employeeLabel: filters.employeeLabel } : null)}
                                className="inline-flex items-center px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl shadow-md hover:bg-blue-700 transition-colors"
                            >
                                <PlusCircle className="w-4 h-4 mr-2" />
                                Manuel Hareket Ekle
                            </button>
                        )}
                    </div>
                </div>

                {/* Otomatik yıllık izin hakediş durumu */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
                    <div className="flex items-start gap-3">
                        <Settings className={`w-5 h-5 mt-0.5 ${autoAnnualActive ? 'text-emerald-500' : 'text-amber-500'}`} />
                        <div>
                            <p className="text-xs font-bold text-gray-800 dark:text-gray-100">
                                Otomatik Yıllık İzin Hakediş Motoru (Hangfire Günlük & Çok Yıllı Yakalama)
                            </p>
                            {settingsLoading ? (
                                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                                    Ayarlar yükleniyor...
                                </p>
                            ) : settingsError ? (
                                <p className="text-[11px] text-red-500 dark:text-red-300 mt-0.5">
                                    {settingsError}
                                </p>
                            ) : autoAnnualActive ? (
                                <p className="text-xs text-emerald-700 dark:text-emerald-300 mt-0.5">
                                    Sistem <span className="font-bold">AKTİF</span>. Başlangıç tarihi:{' '}
                                    <span className="font-mono font-semibold">{autoAnnualDate || '-'}</span>{' '}
                                    <span className="text-gray-500 dark:text-gray-400">
                                        (Bu tarihten sonraki tüm yıl dönümleri otomatik hesaplanır)
                                    </span>
                                </p>
                            ) : (
                                <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                                    Sistem şu anda <span className="font-bold">PASİF</span>. Yıllık izin hakedişleri sadece manuel hareketler üzerinden takip edilmektedir.
                                </p>
                            )}

                            {settings?.sonExcelYuklemeTarihi && (
                                <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 text-[11px] text-gray-600 dark:text-gray-300 flex flex-wrap items-center gap-2">
                                    <span className="font-semibold text-blue-600 dark:text-blue-400">
                                        📊 Son Excel Bakiye Aktarımı:
                                    </span>
                                    <span>
                                        {new Date(settings.sonExcelYuklemeTarihi).toLocaleString('tr-TR', {
                                            day: '2-digit',
                                            month: '2-digit',
                                            year: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })}{' '}
                                        ({settings.sonExcelYukleyenKullanici || 'Sistem'})
                                    </span>
                                    {settings.sonExcelBazTarihi && (
                                        <span className="px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 font-bold">
                                            Excel Baz Tarihi: {settings.sonExcelBazTarihi}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {canManage && !settingsLoading && (
                        <div className="flex flex-wrap items-center gap-2">
                            {autoAnnualActive && (
                                <button
                                    type="button"
                                    disabled={isRunningAutoNow}
                                    onClick={handleRunAutoAnnualNow}
                                    className="inline-flex items-center px-3.5 py-2 bg-purple-600 text-white text-xs font-semibold rounded-xl shadow-sm hover:bg-purple-700 disabled:opacity-50 transition-colors"
                                >
                                    {isRunningAutoNow ? (
                                        <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                                    ) : (
                                        <Play className="w-3.5 h-3.5 mr-1.5" />
                                    )}
                                    Hakedişleri Şimdi Hesapla
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => setIsAutoAnnualModalOpen(true)}
                                className="inline-flex items-center px-3.5 py-2 bg-emerald-600 text-white text-xs font-semibold rounded-xl shadow-sm hover:bg-emerald-700 transition-colors"
                            >
                                <Settings className="w-3.5 h-3.5 mr-1.5" />
                                {autoAnnualActive ? 'Ayarları Düzenle' : 'Otomatik Hakedişi Başlat'}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Uyarı & Mesajlar */}
            {message && (
                <div className="mb-4 flex items-center bg-emerald-50 dark:bg-emerald-900/40 border border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200 px-4 py-3 rounded-xl text-sm">
                    <CheckCircle2 className="w-4 h-4 mr-2 shrink-0 text-emerald-500" />
                    <span>{message}</span>
                </div>
            )}
            {error && (
                <div className="mb-4 flex items-center bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-200 px-4 py-3 rounded-xl text-sm">
                    <AlertCircle className="w-4 h-4 mr-2 shrink-0 text-red-500" />
                    <span>{error}</span>
                </div>
            )}

            {/* GÖRÜNÜM SEKMELERİ (TAB BAR) */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5 border-b border-gray-200 dark:border-gray-700 pb-3">
                <div className="inline-flex p-1 rounded-xl bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
                    <button
                        type="button"
                        onClick={() => setActiveTab('summary')}
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs sm:text-sm font-bold transition-all ${
                            activeTab === 'summary'
                                ? 'bg-indigo-600 text-white shadow-md'
                                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                        }`}
                    >
                        <Users className="w-4 h-4" />
                        Personel Bazlı Toplam Bakiyeler (Kişi Özeti)
                        <span className={`px-2 py-0.5 rounded-full text-[11px] ${
                            activeTab === 'summary'
                                ? 'bg-indigo-500 text-white'
                                : 'bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                        }`}>
                            {summaryPagination.totalCount} Kişi
                        </span>
                    </button>

                    <button
                        type="button"
                        onClick={() => setActiveTab('movements')}
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs sm:text-sm font-bold transition-all ${
                            activeTab === 'movements'
                                ? 'bg-purple-600 text-white shadow-md'
                                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                        }`}
                    >
                        <History className="w-4 h-4" />
                        Detaylı Bakiye Hareket Kayıtları (Yıl Yıl İşlem Defteri)
                        <span className={`px-2 py-0.5 rounded-full text-[11px] ${
                            activeTab === 'movements'
                                ? 'bg-purple-500 text-white'
                                : 'bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                        }`}>
                            {pagination.totalCount} Hareket
                        </span>
                    </button>
                </div>

                {activeTab === 'summary' && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                        💡 Her personel <strong>tek satırda</strong> listelenir. Yıl yıl hakediş kovalarını görmek için satırdaki <strong>"Yıl Detayı"</strong> butonuna tıklayın.
                    </div>
                )}
            </div>

            {/* ================================================================= */}
            {/* SEKME 1: PERSONEL BAZLI TOPLAM BAKİYE ÖZETİ (HER PERSONEL 1 SATIR) */}
            {/* ================================================================= */}
            {activeTab === 'summary' && (
                <div>
                    {/* 4 Adet Kurumsal KPI Özet Kartı */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
                        <div className="p-4 rounded-xl border border-indigo-200 dark:border-indigo-800/60 bg-indigo-50/60 dark:bg-indigo-950/30">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-semibold text-indigo-700 dark:text-indigo-300">
                                    Listelenen Aktif Personel
                                </span>
                                <Users className="w-4 h-4 text-indigo-500" />
                            </div>
                            <div className="mt-1.5 text-2xl font-extrabold text-gray-900 dark:text-white font-mono">
                                {summaryPagination.totalCount} <span className="text-xs font-normal text-gray-500">Kişi</span>
                            </div>
                            <div className="mt-1 text-[11px] text-gray-600 dark:text-gray-400 flex items-center gap-2">
                                <span className="text-emerald-600 dark:text-emerald-400 font-semibold">
                                    {summaryPagination.employeesWithPositiveBalance} pozitif
                                </span>
                                <span>•</span>
                                <span>{summaryPagination.employeesWithZeroOrNegativeBalance} sıfır/eksi</span>
                            </div>
                        </div>

                        <div className="p-4 rounded-xl border border-purple-200 dark:border-purple-800/60 bg-purple-50/60 dark:bg-purple-950/30">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-semibold text-purple-700 dark:text-purple-300">
                                    Toplam Hak Edilen İzin
                                </span>
                                <Award className="w-4 h-4 text-purple-500" />
                            </div>
                            <div className="mt-1.5 text-2xl font-extrabold text-purple-700 dark:text-purple-300 font-mono">
                                {summaryPagination.grandTotalEntitledDays.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                                <span className="text-xs font-normal">Gün</span>
                            </div>
                            <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                                Tüm yılların kümülatif hakediş toplamı
                            </div>
                        </div>

                        <div className="p-4 rounded-xl border border-amber-200 dark:border-amber-800/60 bg-amber-50/60 dark:bg-amber-950/30">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-semibold text-amber-700 dark:text-amber-300">
                                    Toplam Kullanılan İzin
                                </span>
                                <Calendar className="w-4 h-4 text-amber-500" />
                            </div>
                            <div className="mt-1.5 text-2xl font-extrabold text-amber-700 dark:text-amber-300 font-mono">
                                {summaryPagination.grandTotalUsedDays.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                                <span className="text-xs font-normal">Gün</span>
                            </div>
                            <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                                Onaylanan izinlerden düşülen toplam süre
                            </div>
                        </div>

                        <div className="p-4 rounded-xl border border-emerald-300 dark:border-emerald-700 bg-emerald-50/80 dark:bg-emerald-950/40 shadow-sm">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-bold text-emerald-800 dark:text-emerald-300">
                                    Toplam Net Kalan Bakiye
                                </span>
                                <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                            </div>
                            <div className="mt-1.5 text-2xl font-extrabold text-emerald-700 dark:text-emerald-300 font-mono">
                                {summaryPagination.grandTotalRemainingDays.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                                <span className="text-xs font-normal">Gün</span>
                            </div>
                            <div className="mt-1 text-[11px] text-emerald-700/80 dark:text-emerald-400/80 font-medium">
                                Personellerin kullanılabilir net izin toplamı
                            </div>
                        </div>
                    </div>

                    {/* Kişi Bazlı Özet Filtre & Sıralama Barı */}
                    <div className="mb-5 p-4 bg-gray-50 dark:bg-gray-900/40 rounded-xl border border-gray-200 dark:border-gray-700">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
                            <div className="lg:col-span-2">
                                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                    Personel Ara (Ad Soyad, Sicil No, TC No)
                                </label>
                                <div className="relative">
                                    <Search className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
                                    <input
                                        type="text"
                                        value={summaryFilters.search}
                                        onChange={(e) => setSummaryFilters(prev => ({ ...prev, search: e.target.value }))}
                                        placeholder="Örn: Ahmet Yılmaz, PRS-0001..."
                                        className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                    Departman
                                </label>
                                <select
                                    value={summaryFilters.departmentId}
                                    onChange={(e) => setSummaryFilters(prev => ({ ...prev, departmentId: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100"
                                >
                                    <option value="">Tüm Departmanlar</option>
                                    {departments.map(d => (
                                        <option key={d.id} value={d.id}>
                                            {d.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                    Bakiye Durumu
                                </label>
                                <select
                                    value={summaryFilters.balanceStatus}
                                    onChange={(e) => setSummaryFilters(prev => ({ ...prev, balanceStatus: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100"
                                >
                                    <option value="all">Tüm Aktif Personeller</option>
                                    <option value="positive">Kalan Bakiyesi Olanlar (&gt; 0 Gün)</option>
                                    <option value="zero">Bakiyesi Sıfır Olanlar (0 Gün)</option>
                                    <option value="negative">Eksi Bakiyeye Düşenler (&lt; 0 Gün)</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                    Sıralama Ölçütü
                                </label>
                                <select
                                    value={summaryFilters.sortBy}
                                    onChange={(e) => setSummaryFilters(prev => ({ ...prev, sortBy: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100"
                                >
                                    <option value="remaining_desc">Kalan Bakiye (Çoktan Aza)</option>
                                    <option value="remaining_asc">Kalan Bakiye (Azdan Çoğa)</option>
                                    <option value="name_asc">Ad Soyad (A → Z)</option>
                                    <option value="name_desc">Ad Soyad (Z → A)</option>
                                    <option value="seniority_desc">Kıdem (En Kıdemliler Üstte)</option>
                                    <option value="seniority_asc">Kıdem (Yeni Girişliler Üstte)</option>
                                    <option value="sicil_asc">Sicil No (Sıralı)</option>
                                </select>
                            </div>

                            <div className="flex items-center gap-2">
                                <div className="w-28">
                                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                        Listeleme
                                    </label>
                                    <select
                                        value={summaryPagination.pageSize}
                                        onChange={(e) => {
                                            const newSize = Number(e.target.value);
                                            setSummaryPagination(prev => ({ ...prev, pageSize: newSize }));
                                            fetchEmployeeSummary(1, newSize);
                                        }}
                                        className="w-full px-2.5 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-sm font-semibold text-gray-900 dark:text-gray-100"
                                    >
                                        <option value={25}>25 Kişi</option>
                                        <option value={50}>50 Kişi</option>
                                        <option value={100}>100 Kişi</option>
                                        <option value={250}>250 Kişi</option>
                                    </select>
                                </div>
                                <button
                                    type="button"
                                    onClick={handleResetSummaryFilters}
                                    className="h-10 px-3 rounded-xl bg-gray-500 text-white text-xs font-semibold hover:bg-gray-600 shrink-0"
                                >
                                    Sıfırla
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Personel Bazlı Tek Satır Bakiye Tablosu */}
                    <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                        <table className="min-w-full bg-white dark:bg-gray-800">
                            <thead className="bg-gray-100 dark:bg-gray-700/80">
                                <tr>
                                    <th className="py-3 px-3 text-left text-xs font-bold text-gray-700 dark:text-gray-200">
                                        Personel / Sicil No
                                    </th>
                                    <th className="py-3 px-3 text-left text-xs font-bold text-gray-700 dark:text-gray-200">
                                        Departman
                                    </th>
                                    <th className="py-3 px-3 text-center text-xs font-bold text-gray-700 dark:text-gray-200">
                                        Kıdem Başlangıcı & Kademe
                                    </th>
                                    <th className="py-3 px-3 text-center text-xs font-bold text-gray-700 dark:text-gray-200">
                                        Sonraki Hakediş
                                    </th>
                                    <th className="py-3 px-3 text-center text-xs font-bold text-gray-700 dark:text-gray-200">
                                        Yıl Kovaları
                                    </th>
                                    <th className="py-3 px-3 text-center text-xs font-bold text-gray-700 dark:text-gray-200">
                                        Toplam Hak Edilen
                                    </th>
                                    <th className="py-3 px-3 text-center text-xs font-bold text-gray-700 dark:text-gray-200">
                                        Toplam Kullanılan
                                    </th>
                                    <th className="py-3 px-3 text-center text-xs font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-50/60 dark:bg-emerald-950/30">
                                        NET KALAN BAKİYE
                                    </th>
                                    <th className="py-3 px-3 text-center text-xs font-bold text-gray-700 dark:text-gray-200">
                                        Detay & İşlemler
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {summaryLoading && summaryItems.length === 0 ? (
                                    <tr>
                                        <td colSpan={9} className="py-10 text-center">
                                            <Loader2 className="w-6 h-6 animate-spin text-indigo-500 mx-auto" />
                                        </td>
                                    </tr>
                                ) : summaryItems.length === 0 ? (
                                    <tr>
                                        <td colSpan={9} className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                                            Filtre kriterlerine uygun personel bulunamadı.
                                        </td>
                                    </tr>
                                ) : (
                                    summaryItems.map((emp) => {
                                        const isExpanded = !!expandedEmployeeIds[emp.employeeId];
                                        const remDays = emp.remainingDays || 0;
                                        const remBadgeClass =
                                            remDays > 0
                                                ? 'bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-900/50 dark:text-emerald-200 dark:border-emerald-700'
                                                : remDays < 0
                                                    ? 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/50 dark:text-red-200 dark:border-red-700'
                                                    : 'bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600';

                                        return (
                                            <React.Fragment key={emp.employeeId}>
                                                <tr
                                                    onClick={() => toggleExpandEmployee(emp.employeeId)}
                                                    className={`cursor-pointer transition-colors ${
                                                        isExpanded
                                                            ? 'bg-indigo-50/60 dark:bg-indigo-950/30'
                                                            : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                                                    }`}
                                                >
                                                    <td className="py-3 px-3 text-xs">
                                                        <div className="font-bold text-gray-900 dark:text-gray-100 text-sm">
                                                            {emp.fullName}
                                                        </div>
                                                        <div className="text-[11px] font-mono text-gray-500 dark:text-gray-400">
                                                            Sicil: {emp.sicilNo || `#${emp.employeeId}`}
                                                        </div>
                                                    </td>

                                                    <td className="py-3 px-3 text-xs">
                                                        <div className="font-medium text-gray-800 dark:text-gray-200">
                                                            {emp.departmentName || '-'}
                                                        </div>
                                                        {emp.positionName && (
                                                            <div className="text-[11px] text-gray-500 dark:text-gray-400">
                                                                {emp.positionName}
                                                            </div>
                                                        )}
                                                    </td>

                                                    <td className="py-3 px-3 text-xs text-center">
                                                        <div className="font-mono font-semibold text-gray-800 dark:text-gray-200">
                                                            {emp.effectiveSeniorityDate
                                                                ? new Date(emp.effectiveSeniorityDate).toLocaleDateString('tr-TR')
                                                                : '-'}
                                                        </div>
                                                        <div className="inline-flex items-center gap-1 mt-0.5 px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
                                                            <Award className="w-3 h-3" />
                                                            {emp.effectiveTierYears}. Yıl Kademesi
                                                        </div>
                                                    </td>

                                                    <td className="py-3 px-3 text-xs text-center font-mono text-gray-700 dark:text-gray-300">
                                                        {emp.nextAnniversaryDate
                                                            ? new Date(emp.nextAnniversaryDate).toLocaleDateString('tr-TR')
                                                            : '-'}
                                                    </td>

                                                    <td className="py-3 px-3 text-xs text-center">
                                                        {emp.activeBucketCount > 0 ? (
                                                            <div>
                                                                <span className="font-bold text-purple-700 dark:text-purple-300">
                                                                    {emp.activeBucketCount} Dönem
                                                                </span>
                                                                {emp.minYear && emp.maxYear && (
                                                                    <div className="text-[10px] font-mono text-gray-500 dark:text-gray-400">
                                                                        ({emp.minYear === emp.maxYear ? emp.minYear : `${emp.minYear} - ${emp.maxYear}`})
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <span className="text-gray-400 text-[11px]">Kayıt Yok</span>
                                                        )}
                                                    </td>

                                                    <td className="py-3 px-3 text-center">
                                                        <div className="text-sm font-mono font-bold text-gray-800 dark:text-gray-100">
                                                            {(emp.totalEntitledDays || 0).toFixed(2)} <span className="text-[11px] font-normal">Gün</span>
                                                        </div>
                                                    </td>

                                                    <td className="py-3 px-3 text-center">
                                                        <div className="text-sm font-mono font-bold text-amber-700 dark:text-amber-300">
                                                            {(emp.totalUsedDays || 0).toFixed(2)} <span className="text-[11px] font-normal">Gün</span>
                                                        </div>
                                                    </td>

                                                    <td className="py-3 px-3 text-center bg-emerald-50/30 dark:bg-emerald-950/10">
                                                        <span className={`inline-flex items-center px-3 py-1 rounded-xl border text-sm font-mono font-extrabold shadow-sm ${remBadgeClass}`}>
                                                            {remDays > 0 ? '+' : ''}{remDays.toFixed(2)} Gün
                                                        </span>
                                                        <div className="text-[10px] font-mono text-gray-500 dark:text-gray-400 mt-0.5">
                                                            ({Math.round((emp.remainingMinutes || 0) / 60)} saat / {emp.remainingMinutes || 0} dk)
                                                        </div>
                                                    </td>

                                                    <td className="py-3 px-3 text-center" onClick={(e) => e.stopPropagation()}>
                                                        <div className="flex items-center justify-center gap-1.5">
                                                            <button
                                                                type="button"
                                                                onClick={() => toggleExpandEmployee(emp.employeeId)}
                                                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:bg-indigo-900/40 dark:text-indigo-200"
                                                                title="Yıl yıl hakediş kovalarını aç/kapat"
                                                            >
                                                                <Layers className="w-3.5 h-3.5" />
                                                                Yıl Detayı
                                                                {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                                            </button>

                                                            <button
                                                                type="button"
                                                                onClick={() => switchToEmployeeMovements(emp.employeeId, emp.fullName)}
                                                                className="p-1.5 rounded-lg text-purple-600 hover:bg-purple-50 dark:text-purple-300 dark:hover:bg-purple-900/30"
                                                                title="Bu personelin tüm hareket geçmişini görüntüle"
                                                            >
                                                                <History className="w-4 h-4" />
                                                            </button>

                                                            {canManage && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => openManualModal({
                                                                        employeeId: emp.employeeId,
                                                                        employeeLabel: emp.fullName
                                                                    })}
                                                                    className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/30"
                                                                    title="Bu personele manuel bakiye ekle/düş"
                                                                >
                                                                    <PlusCircle className="w-4 h-4" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>

                                                {/* Açılır Yıl Yıl Hakediş Kovaları Detay Satırı */}
                                                {isExpanded && (
                                                    <tr className="bg-indigo-50/40 dark:bg-indigo-950/20">
                                                        <td colSpan={9} className="p-4 border-b border-indigo-200 dark:border-indigo-800/60">
                                                            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                                                                <div className="flex items-center gap-2 text-xs font-bold text-indigo-900 dark:text-indigo-200">
                                                                    <Layers className="w-4 h-4 text-indigo-500" />
                                                                    <span>
                                                                        {emp.fullName} ({emp.sicilNo}) — Yıllara Göre İzin Kovaları (FIFO Tüketim Sırası)
                                                                    </span>
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => switchToEmployeeMovements(emp.employeeId, emp.fullName)}
                                                                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold bg-purple-600 text-white hover:bg-purple-700"
                                                                    >
                                                                        <History className="w-3.5 h-3.5" />
                                                                        Tüm Hareket Geçmişine Git
                                                                    </button>
                                                                    {canManage && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => openManualModal({
                                                                                employeeId: emp.employeeId,
                                                                                employeeLabel: emp.fullName
                                                                            })}
                                                                            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700"
                                                                        >
                                                                            <PlusCircle className="w-3.5 h-3.5" />
                                                                            Manuel Bakiye Müdahalesi
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {(!emp.yearBuckets || emp.yearBuckets.length === 0) ? (
                                                                <div className="text-xs text-gray-500 dark:text-gray-400 italic py-2">
                                                                    Bu personel için henüz tanımlanmış bir yıl izin kovası bulunmuyor.
                                                                </div>
                                                            ) : (
                                                                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-10 gap-2.5">
                                                                    {emp.yearBuckets.map((b) => (
                                                                        <div
                                                                            key={b.id}
                                                                            className={`p-2.5 rounded-xl border text-xs ${
                                                                                b.kalanGun > 0
                                                                                    ? 'bg-white dark:bg-gray-800 border-emerald-200 dark:border-emerald-800/70 shadow-sm'
                                                                                    : 'bg-gray-100/70 dark:bg-gray-900/50 border-gray-200 dark:border-gray-700 opacity-75'
                                                                            }`}
                                                                        >
                                                                            <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-700 pb-1 mb-1.5">
                                                                                <span className="font-extrabold text-indigo-700 dark:text-indigo-300 font-mono">
                                                                                    {b.yil || 'Genel'}
                                                                                </span>
                                                                                <span className="text-[10px] text-gray-400 truncate max-w-[70px]" title={b.izinTuruAd}>
                                                                                    {b.izinTuruAd}
                                                                                </span>
                                                                            </div>
                                                                            <div className="space-y-0.5 text-[11px]">
                                                                                <div className="flex justify-between text-gray-600 dark:text-gray-400">
                                                                                    <span>Hak:</span>
                                                                                    <span className="font-mono font-semibold text-gray-900 dark:text-gray-100">
                                                                                        {b.toplamGun.toFixed(1)} g
                                                                                    </span>
                                                                                </div>
                                                                                <div className="flex justify-between text-gray-600 dark:text-gray-400">
                                                                                    <span>Kul:</span>
                                                                                    <span className="font-mono font-semibold text-amber-600 dark:text-amber-400">
                                                                                        {b.kullanilanGun.toFixed(1)} g
                                                                                    </span>
                                                                                </div>
                                                                                <div className="flex justify-between pt-1 border-t border-gray-100 dark:border-gray-700 font-bold">
                                                                                    <span>Kalan:</span>
                                                                                    <span className={`font-mono ${b.kalanGun > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-500'}`}>
                                                                                        {b.kalanGun.toFixed(1)} g
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Kişi Özeti Sayfalama Barı */}
                    {summaryPagination.totalPages > 1 && (
                        <div className="mt-4 flex items-center justify-between text-xs text-gray-600 dark:text-gray-300">
                            <div>
                                Sayfa <strong>{summaryPagination.page}</strong> / <strong>{summaryPagination.totalPages}</strong> (Toplam {summaryPagination.totalCount} personel)
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    disabled={summaryPagination.page <= 1 || summaryLoading}
                                    onClick={() => fetchEmployeeSummary(summaryPagination.page - 1)}
                                    className="inline-flex items-center px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 disabled:opacity-50"
                                >
                                    <ChevronLeft className="w-4 h-4 mr-1" /> Önceki
                                </button>
                                <button
                                    type="button"
                                    disabled={summaryPagination.page >= summaryPagination.totalPages || summaryLoading}
                                    onClick={() => fetchEmployeeSummary(summaryPagination.page + 1)}
                                    className="inline-flex items-center px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 disabled:opacity-50"
                                >
                                    Sonraki <ChevronRight className="w-4 h-4 ml-1" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ================================================================= */}
            {/* SEKME 2: DETAYLI BAKİYE HAREKET KAYITLARI (YIL YIL İŞLEM DEFTERİ) */}
            {/* ================================================================= */}
            {activeTab === 'movements' && (
                <div>
                    {/* Filtreler */}
                    <div className="mb-5 p-4 bg-gray-50 dark:bg-gray-900/40 rounded-xl border border-gray-200 dark:border-gray-700">
                        <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
                            <div className="md:col-span-2">
                                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                    Personel (10.000+ Personel Arama)
                                </label>
                                <ServerEmployeeSelect
                                    value={filters.employeeId}
                                    selectedLabel={filters.employeeLabel}
                                    allowAll={true}
                                    onSelect={(id, label) => setFilters(prev => ({ ...prev, employeeId: id, employeeLabel: label }))}
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                    İzin Türü
                                </label>
                                <select
                                    name="izinTuruId"
                                    value={filters.izinTuruId}
                                    onChange={handleFilterChange}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100"
                                >
                                    <option value="">Tümü</option>
                                    {izinTurleri.map(t => (
                                        <option key={t.id} value={t.id}>
                                            {t.ad} ({t.kod})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                    Hareket Tipi
                                </label>
                                <select
                                    name="hareketTipi"
                                    value={filters.hareketTipi}
                                    onChange={handleFilterChange}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100"
                                >
                                    <option value="">Tümü</option>
                                    <option value="OtomatikHakedis">Otomatik Hakediş</option>
                                    <option value="IzinKullanimi">İzin Kullanımı (-)</option>
                                    <option value="IzinIptali">İzin İptali İadesi (+)</option>
                                    <option value="ManuelArtis">Manuel Artış (+)</option>
                                    <option value="ManuelAzalis">Manuel Azalış (-)</option>
                                    <option value="Duzeltme">Düzeltme</option>
                                    <option value="DevirSilme">Devir Süre Sonu / Silme</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                    Hakediş Yılı
                                </label>
                                <input
                                    type="number"
                                    name="yil"
                                    value={filters.yil}
                                    onChange={handleFilterChange}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100"
                                    placeholder="Örn: 2026"
                                />
                            </div>

                            <div className="flex gap-2 justify-end">
                                <button
                                    type="button"
                                    onClick={() => fetchMovements(1)}
                                    className="h-10 px-4 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 flex items-center justify-center"
                                >
                                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Filtrele'}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleResetFilters}
                                    className="h-10 px-4 rounded-xl bg-gray-500 text-white text-sm font-semibold hover:bg-gray-600"
                                >
                                    Sıfırla
                                </button>
                            </div>
                        </div>

                        {/* Toplam net etki */}
                        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-600 dark:text-gray-300">
                            <div>
                                <span className="font-semibold">Filtrelenen hareketlerin net bakiye etkisi:</span>{' '}
                                <span className="font-mono font-bold text-indigo-600 dark:text-indigo-400">
                                    {pagination.totalNetDays.toFixed(2)} gün (~{Math.round(pagination.totalNetMinutes)} dk)
                                </span>
                            </div>
                            <div className="text-[11px] text-gray-500 dark:text-gray-400">
                                Toplam <strong>{pagination.totalCount}</strong> hareket kaydı bulundu
                            </div>
                        </div>
                    </div>

                    {/* Seçili personel için güncel izin bakiyesi & kıdem özeti */}
                    {filters.employeeId && (
                        <div className="mb-5 rounded-xl border border-purple-200 dark:border-purple-700 bg-purple-50/70 dark:bg-purple-900/20 p-4 space-y-3">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <div className="flex items-center gap-2">
                                    <User className="w-4 h-4 text-purple-600 dark:text-purple-300" />
                                    <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
                                        {filters.employeeLabel || `Personel #${filters.employeeId}`} — Yıllara Göre İzin Kovaları ve Kıdem Özeti
                                    </span>
                                </div>
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-purple-600 text-white shadow-sm">
                                    Toplam Kullanılabilir Kalan: {totalEmployeeRemainingDays.toFixed(2)} Gün
                                </span>
                            </div>

                            {employeeSeniority && (
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                                    <div className="bg-white/90 dark:bg-gray-800/80 rounded-lg p-2.5 border border-purple-100 dark:border-purple-800">
                                        <span className="text-gray-500 dark:text-gray-400 block text-[10px]">Güncel İşe Giriş</span>
                                        <span className="font-mono font-bold text-gray-900 dark:text-white">
                                            {employeeSeniority.hireDate ? new Date(employeeSeniority.hireDate).toLocaleDateString('tr-TR') : '-'}
                                        </span>
                                    </div>
                                    <div className="bg-white/90 dark:bg-gray-800/80 rounded-lg p-2.5 border border-purple-100 dark:border-purple-800">
                                        <span className="text-gray-500 dark:text-gray-400 block text-[10px]">Efektif Kıdem Başlangıcı</span>
                                        <span className="font-mono font-bold text-emerald-700 dark:text-emerald-400">
                                            {employeeSeniority.effectiveSeniorityDate ? new Date(employeeSeniority.effectiveSeniorityDate).toLocaleDateString('tr-TR') : '-'}
                                        </span>
                                    </div>
                                    <div className="bg-white/90 dark:bg-gray-800/80 rounded-lg p-2.5 border border-purple-100 dark:border-purple-800">
                                        <span className="text-gray-500 dark:text-gray-400 block text-[10px]">Kıdem / İzin Kademesi</span>
                                        <span className="font-bold text-indigo-700 dark:text-indigo-300 flex items-center gap-1">
                                            <Award className="w-3.5 h-3.5" /> {employeeSeniority.effectiveTierYears || 0}. Yıl Kademesi
                                        </span>
                                    </div>
                                    <div className="bg-white/90 dark:bg-gray-800/80 rounded-lg p-2.5 border border-purple-100 dark:border-purple-800">
                                        <span className="text-gray-500 dark:text-gray-400 block text-[10px]">Sonraki Yıl Dönümü</span>
                                        <span className="font-mono font-bold text-purple-700 dark:text-purple-300">
                                            {employeeSeniority.nextAnniversaryDate ? new Date(employeeSeniority.nextAnniversaryDate).toLocaleDateString('tr-TR') : '-'}
                                        </span>
                                    </div>
                                </div>
                            )}

                            {employeeBalancesLoading ? (
                                <div className="flex items-center text-xs text-gray-600 dark:text-gray-300">
                                    <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                                    <span>Personel izin bakiyeleri yükleniyor...</span>
                                </div>
                            ) : employeeBalancesError ? (
                                <div className="flex items-center text-xs text-red-600 dark:text-red-300">
                                    <AlertCircle className="w-3.5 h-3.5 mr-1" />
                                    <span>{employeeBalancesError}</span>
                                </div>
                            ) : mappedEmployeeBalances.length === 0 ? (
                                <div className="text-xs text-gray-600 dark:text-gray-300 italic">
                                    Bu personel için aktif izin bakiyesi kaydı bulunamadı.
                                </div>
                            ) : (
                                <div className="overflow-x-auto bg-white dark:bg-gray-900/60 rounded-xl border border-purple-200/70 dark:border-purple-800/60">
                                    <table className="min-w-full text-xs">
                                        <thead className="bg-purple-100/60 dark:bg-purple-900/40">
                                            <tr>
                                                <th className="py-2 px-3 text-left font-semibold text-gray-700 dark:text-gray-200">İzin Türü</th>
                                                <th className="py-2 px-3 text-center font-semibold text-gray-700 dark:text-gray-200">Bakiye Modu</th>
                                                <th className="py-2 px-3 text-center font-semibold text-gray-700 dark:text-gray-200">Hakediş Dönemi</th>
                                                <th className="py-2 px-3 text-center font-semibold text-gray-700 dark:text-gray-200">Toplam Hak (Gün)</th>
                                                <th className="py-2 px-3 text-center font-semibold text-gray-700 dark:text-gray-200">Kullanılan (Gün)</th>
                                                <th className="py-2 px-3 text-center font-semibold text-gray-700 dark:text-gray-200">Kalan (Gün)</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-purple-100 dark:divide-purple-800/50">
                                            {mappedEmployeeBalances.map(b => {
                                                const izinTuru = izinTurleri.find(it => String(it.id) === String(b.izinTuruId));
                                                const izinTuruAd = izinTuru ? `${izinTuru.ad} (${izinTuru.kod})` : (b.izinTuruAd || `Tür ID: ${b.izinTuruId}`);
                                                const donemEtiketi =
                                                    b.donemTipi === 1 || b.donemTipi === 'Yillik'
                                                        ? `${donemTipiLabel[b.donemTipi] || ''} ${b.yil || ''}`
                                                        : b.donemTipi === 2 || b.donemTipi === 'Aylik'
                                                            ? `${donemTipiLabel[b.donemTipi] || ''} ${b.ay || ''}/${b.yil || ''}`
                                                            : donemTipiLabel[b.donemTipi] || '';

                                                return (
                                                    <tr key={b.id} className="hover:bg-purple-50/50 dark:hover:bg-purple-900/20">
                                                        <td className="py-2 px-3 font-medium text-gray-800 dark:text-gray-100">{izinTuruAd}</td>
                                                        <td className="py-2 px-3 text-center text-gray-700 dark:text-gray-300">{bakiyeModuLabel[b.bakiyeModu] || 'Bilinmiyor'}</td>
                                                        <td className="py-2 px-3 text-center font-semibold text-indigo-700 dark:text-indigo-300">{donemEtiketi || '-'}</td>
                                                        <td className="py-2 px-3 text-center font-mono text-gray-800 dark:text-gray-100">{b.toplamGun.toFixed(2)}</td>
                                                        <td className="py-2 px-3 text-center font-mono text-amber-700 dark:text-amber-300">{b.kullanilanGun.toFixed(2)}</td>
                                                        <td className="py-2 px-3 text-center font-mono font-bold text-emerald-700 dark:text-emerald-400">{b.kalanGun.toFixed(2)}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Hareketler Tablosu */}
                    <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                        <table className="min-w-full bg-white dark:bg-gray-800">
                            <thead className="bg-gray-100 dark:bg-gray-700">
                                <tr>
                                    <th className="py-2.5 px-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-200">ID</th>
                                    <th className="py-2.5 px-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-200">Personel</th>
                                    <th className="py-2.5 px-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-200">İzin Türü</th>
                                    <th className="py-2.5 px-3 text-center text-xs font-semibold text-gray-700 dark:text-gray-200">Dönem Yılı</th>
                                    <th className="py-2.5 px-3 text-center text-xs font-semibold text-gray-700 dark:text-gray-200">Değişim (Gün)</th>
                                    <th className="py-2.5 px-3 text-center text-xs font-semibold text-gray-700 dark:text-gray-200">Değişim (Dk)</th>
                                    <th className="py-2.5 px-3 text-center text-xs font-semibold text-gray-700 dark:text-gray-200">Hareket Tipi</th>
                                    <th className="py-2.5 px-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-200">Açıklama</th>
                                    <th className="py-2.5 px-3 text-center text-xs font-semibold text-gray-700 dark:text-gray-200">İşlem Tarihi</th>
                                    <th className="py-2.5 px-3 text-center text-xs font-semibold text-gray-700 dark:text-gray-200">İşlemi Yapan</th>
                                    {canManage && (
                                        <th className="py-2.5 px-3 text-center text-xs font-semibold text-gray-700 dark:text-gray-200">Aksiyonlar</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {loading && movements.length === 0 ? (
                                    <tr>
                                        <td colSpan={canManage ? 11 : 10} className="py-8 text-center">
                                            <Loader2 className="w-6 h-6 animate-spin text-purple-500 mx-auto" />
                                        </td>
                                    </tr>
                                ) : movements.length === 0 ? (
                                    <tr>
                                        <td colSpan={canManage ? 11 : 10} className="py-6 text-center text-sm text-gray-500 dark:text-gray-300">
                                            Seçilen kriterlere uygun izin bakiye hareketi bulunamadı.
                                        </td>
                                    </tr>
                                ) : (
                                    movements.map((m) => {
                                        const isPositive = (m.miktarDakika || 0) >= 0;
                                        return (
                                            <tr key={m.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/60">
                                                <td className="py-2.5 px-3 text-xs text-gray-600 dark:text-gray-300 font-mono">#{m.id}</td>
                                                <td className="py-2.5 px-3 text-xs font-medium text-gray-900 dark:text-gray-100">
                                                    <div>{m.employeeAdSoyad || `Personel #${m.employeeId}`}</div>
                                                    {m.employeeSicilNo && (
                                                        <div className="text-[10px] font-mono text-gray-400">{m.employeeSicilNo}</div>
                                                    )}
                                                </td>
                                                <td className="py-2.5 px-3 text-xs text-gray-800 dark:text-gray-100">
                                                    {m.izinTuruAd || m.izinTuruId}
                                                </td>
                                                <td className="py-2.5 px-3 text-xs text-center font-semibold text-gray-700 dark:text-gray-200">
                                                    {m.yil || '-'}
                                                </td>
                                                <td className={`py-2.5 px-3 text-xs text-center font-mono font-bold ${isPositive ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                                                    {isPositive ? '+' : ''}{m.miktarGun?.toFixed(2)}
                                                </td>
                                                <td className="py-2.5 px-3 text-xs text-center text-gray-700 dark:text-gray-300 font-mono">
                                                    {isPositive ? '+' : ''}{Math.round(m.miktarDakika || 0)}
                                                </td>
                                                <td className="py-2.5 px-3 text-xs text-center">
                                                    <span
                                                        className={`inline-flex px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${hareketTipiRenk[m.hareketTipi] ||
                                                            'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                                                            }`}
                                                    >
                                                        {hareketTipiLabel[m.hareketTipi] || m.hareketTipi}
                                                    </span>
                                                </td>
                                                <td className="py-2.5 px-3 text-xs text-gray-700 dark:text-gray-200 max-w-md">
                                                    {m.aciklama || '-'}
                                                </td>
                                                <td className="py-2.5 px-3 text-xs text-center text-gray-600 dark:text-gray-300 whitespace-nowrap">
                                                    {m.createdAt ? new Date(m.createdAt).toLocaleString('tr-TR') : '-'}
                                                </td>
                                                <td className="py-2.5 px-3 text-xs text-center text-gray-600 dark:text-gray-300">
                                                    {m.createdByUserId || '-'}
                                                </td>
                                                {canManage && (
                                                    <td className="py-2.5 px-3 text-center">
                                                        <div className="flex items-center justify-center gap-1.5">
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    openManualModal({
                                                                        employeeId: m.employeeId,
                                                                        employeeLabel: m.employeeAdSoyad,
                                                                        izinTuruId: m.izinTuruId,
                                                                        yil: m.yil
                                                                    })
                                                                }
                                                                className="p-1 text-blue-500 hover:text-blue-700"
                                                                title="Bu kişi ve izin türü için yeni manuel hareket ekle"
                                                            >
                                                                <PlusCircle className="w-4 h-4" />
                                                            </button>
                                                            {isManualMovementType(m.hareketTipi) && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleDeleteMovement(m)}
                                                                    className="p-1 text-red-500 hover:text-red-700"
                                                                    title="Bu manuel hareket kaydını sil"
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Sunucu Taraflı Sayfalama Barı */}
                    {pagination.totalPages > 1 && (
                        <div className="mt-4 flex items-center justify-between text-xs text-gray-600 dark:text-gray-300">
                            <div>
                                Sayfa <strong>{pagination.page}</strong> / <strong>{pagination.totalPages}</strong> (Toplam {pagination.totalCount} kayıt)
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    disabled={pagination.page <= 1 || loading}
                                    onClick={() => fetchMovements(pagination.page - 1)}
                                    className="inline-flex items-center px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 disabled:opacity-50"
                                >
                                    <ChevronLeft className="w-4 h-4 mr-1" /> Önceki
                                </button>
                                <button
                                    type="button"
                                    disabled={pagination.page >= pagination.totalPages || loading}
                                    onClick={() => fetchMovements(pagination.page + 1)}
                                    className="inline-flex items-center px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 disabled:opacity-50"
                                >
                                    Sonraki <ChevronRight className="w-4 h-4 ml-1" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            <ManualAdjustModal
                show={isManualModalOpen}
                onClose={() => setIsManualModalOpen(false)}
                onSubmit={handleManualSubmit}
                izinTurleri={izinTurleri}
                initialContext={manualContext}
                isSubmitting={isSubmittingManual}
            />

            <AutoAnnualModal
                show={isAutoAnnualModalOpen}
                onClose={() => setIsAutoAnnualModalOpen(false)}
                onSubmit={handleAutoAnnualSubmit}
                isSubmitting={isSavingAutoAnnual}
                currentSettings={settings}
            />
        </div>
    );
}
